import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { AlertTriangle, ArrowRight, FileText } from 'lucide-react';

const STRIPE_PAYMENT_URL = process.env.REACT_APP_STRIPE_PAYMENT_URL || 'https://myeaitradingcompass.com/trading-subscription';

export default function Challenge({ onOpenModal }) {
  const phases = [
    {
      days: "Day 1-7",
      label: "Mirror",
      description: "Awareness + baseline discipline. Exposes behavioural patterns and risk leaks.",
      testId: "challenge-phase-1"
    },
    {
      days: "Day 8-14",
      label: "Mentor",
      description: "Pre-trade checkpoints + guidance. Shows exactly what must be fixed.",
      testId: "challenge-phase-2"
    },
    {
      days: "Day 15-21",
      label: "Master",
      description: "Strict validation before execution. Creates repeatable process.",
      testId: "challenge-phase-3"
    }
  ];

  return (
    <section className="py-10 md:py-16 bg-white">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <motion.h2 
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.4 }}
            className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#337AF7] tracking-tight"
            data-testid="challenge-title"
          >
            The 21-Day Trading Habit Challenge
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="mt-4 text-lg md:text-xl text-[#337AF7] font-semibold"
          >
            A capital-readiness filter, not a course
          </motion.p>
        </div>

        {/* Timeline */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10">
          {phases.map((phase, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              data-testid={phase.testId}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200 p-6"
            >
              <div className="text-[#337AF7] text-sm font-semibold mb-2">{phase.days}</div>
              <h3 className="text-2xl font-bold text-[#162959] mb-3">{phase.label}</h3>
              <p className="text-sm text-[#162959]/70">{phase.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Warning Box */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="rounded-2xl border border-amber-300 bg-amber-50 p-5 md:p-6 max-w-3xl mx-auto mb-10"
          data-testid="challenge-warning"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm md:text-base text-amber-800">
              <span className="font-semibold">If you're not willing to follow structure for 21 days, don't start.</span> This challenge is intentionally demanding. The first 7 days will expose why you're inconsistent.
            </p>
          </div>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href={STRIPE_PAYMENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="challenge-start-button"
            className="inline-flex items-center rounded-xl bg-[#337AF7] text-white font-semibold px-8 py-4 text-base shadow-lg hover:bg-[#2563eb] hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#337AF7]/50 transition-all duration-200"
          >
            Accept Challenge
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
          <Button
            onClick={onOpenModal}
            data-testid="challenge-view-pdf-button"
            className="rounded-xl bg-white text-[#162959] border border-slate-200 px-8 py-4 text-base hover:bg-slate-50 hover:border-slate-300 focus-visible:ring-2 focus-visible:ring-[#337AF7]/50 transition-colors duration-200"
          >
            <FileText className="mr-2 h-5 w-5" />
            Reserve My Spot
          </Button>
        </motion.div>
      </div>
    </section>
  );
}